import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { assetsAPI } from '../services/api';
import { X, TrendingUp, TrendingDown, BarChart3, Loader2 } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, BarChart, Bar } from 'recharts';

interface SampleReportModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function SampleReportModal({ isOpen, onClose }: SampleReportModalProps) {
  const { data: reportData, isLoading } = useQuery({
    queryKey: ['sample-report'],
    queryFn: () => assetsAPI.getSampleReport(),
    enabled: isOpen,
  });

  if (!isOpen) return null;

  const data = reportData?.data;
  const asset = data?.asset;
  const latestValuation = data?.latest_valuation;
  const valuations = data?.valuations || [];
  const phs = data?.phs;

  // 차트 데이터 준비
  const chartData = valuations
    .slice()
    .reverse()
    .map((v: any) => ({
      date: new Date(v.created_at).toLocaleDateString('ko-KR', { month: 'short', day: 'numeric' }),
      modelValue: v.model_value,
      price: v.price_at_run,
      gap: v.gap_pct,
    }));

  // Explainability 데이터
  const explainabilityData = latestValuation?.explainability?.features || [];

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        {/* 배경 오버레이 */}
        <div
          className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75"
          onClick={onClose}
        />

        {/* 모달 패널 */}
        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-6xl sm:w-full">
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            {/* 헤더 */}
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">
                  샘플 리포트
                </h2>
                <p className="text-sm text-gray-600 mt-1">
                  실제 리포트의 모습을 미리 확인하세요
                </p>
              </div>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-500"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
              </div>
            ) : (
              <div className="space-y-6">
                {/* 자산 정보 */}
                {asset && (
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">
                      {asset.symbol} - {asset.name}
                    </h3>
                    <p className="text-sm text-gray-600">
                      {data.is_sample ? '데모 데이터' : '실제 데이터'}
                    </p>
                  </div>
                )}

                {/* 요약 바 */}
                {latestValuation && (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4 bg-gray-50 rounded-lg">
                    <div>
                      <p className="text-sm text-gray-500">최신 모델값</p>
                      <p className="text-2xl font-bold text-gray-900">
                        ${latestValuation.model_value?.toFixed(2)}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">현재가</p>
                      <p className="text-2xl font-bold text-gray-900">
                        ${latestValuation.price_at_run?.toFixed(2)}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Gap %</p>
                      <p
                        className={`text-2xl font-bold ${
                          (latestValuation.gap_pct || 0) > 0
                            ? 'text-green-600'
                            : 'text-red-600'
                        }`}
                      >
                        {latestValuation.gap_pct?.toFixed(2)}%
                      </p>
                    </div>
                    {phs && (
                      <div>
                        <p className="text-sm text-gray-500">Protocol Health</p>
                        <p className="text-2xl font-bold text-primary-600">
                          {phs.phs_score}/100
                        </p>
                      </div>
                    )}
                  </div>
                )}

                {/* 가치평가 히스토리 차트 */}
                {chartData.length > 0 && (
                  <div className="card">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">
                      가치평가 히스토리
                    </h3>
                    <ResponsiveContainer width="100%" height={300}>
                      <LineChart data={chartData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="date" />
                        <YAxis />
                        <Tooltip />
                        <Legend />
                        <Line
                          type="monotone"
                          dataKey="modelValue"
                          stroke="#3b82f6"
                          name="모델값"
                          strokeWidth={2}
                        />
                        <Line
                          type="monotone"
                          dataKey="price"
                          stroke="#10b981"
                          name="현재가"
                          strokeWidth={2}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                )}

                {/* Explainability */}
                {explainabilityData.length > 0 && (
                  <div className="card">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">
                      가치평가 요인 분석
                    </h3>
                    <ResponsiveContainer width="100%" height={250}>
                      <BarChart data={explainabilityData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="name" />
                        <YAxis />
                        <Tooltip />
                        <Bar dataKey="contribution" fill="#3b82f6" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                )}

                {/* Protocol Health Score */}
                {phs && (
                  <div className="card">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">
                      Protocol Health Score
                    </h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div>
                        <p className="text-sm text-gray-500 mb-1">보안</p>
                        <div className="flex items-center">
                          <div className="flex-1 bg-gray-200 rounded-full h-2 mr-2">
                            <div
                              className="bg-blue-600 h-2 rounded-full"
                              style={{ width: `${phs.breakdown?.security || 0}%` }}
                            />
                          </div>
                          <span className="text-sm font-medium">
                            {phs.breakdown?.security || 0}
                          </span>
                        </div>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500 mb-1">탈중앙화</p>
                        <div className="flex items-center">
                          <div className="flex-1 bg-gray-200 rounded-full h-2 mr-2">
                            <div
                              className="bg-green-600 h-2 rounded-full"
                              style={{ width: `${phs.breakdown?.decentralization || 0}%` }}
                            />
                          </div>
                          <span className="text-sm font-medium">
                            {phs.breakdown?.decentralization || 0}
                          </span>
                        </div>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500 mb-1">경제적</p>
                        <div className="flex items-center">
                          <div className="flex-1 bg-gray-200 rounded-full h-2 mr-2">
                            <div
                              className="bg-yellow-600 h-2 rounded-full"
                              style={{ width: `${phs.breakdown?.economic || 0}%` }}
                            />
                          </div>
                          <span className="text-sm font-medium">
                            {phs.breakdown?.economic || 0}
                          </span>
                        </div>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500 mb-1">커뮤니티</p>
                        <div className="flex items-center">
                          <div className="flex-1 bg-gray-200 rounded-full h-2 mr-2">
                            <div
                              className="bg-purple-600 h-2 rounded-full"
                              style={{ width: `${phs.breakdown?.community || 0}%` }}
                            />
                          </div>
                          <span className="text-sm font-medium">
                            {phs.breakdown?.community || 0}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* CTA */}
                <div className="bg-primary-50 border border-primary-200 rounded-lg p-4">
                  <p className="text-sm text-primary-800 mb-2">
                    <strong>회원가입</strong>하시면 더 많은 자산의 상세 리포트를 확인하실 수 있습니다.
                  </p>
                  <button
                    onClick={onClose}
                    className="btn btn-primary"
                  >
                    시작하기
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

